// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { formatNumber } from "@mcmc/App/Components/FormattedNumber";
import loadSearchCounts from "@mcmc/App/Handlers/Pages/ProductList/LoadSearchCounts";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getSearchCounts } from "@mcmc/App/Store/Selectors/SearchCountsSelectors";
import {
    getContentPageDataViewProperty,
    isContentSearchMode,
    isSearchPage,
    SearchTypes,
} from "@mcmc/App/Store/Selectors/SearchResultsSelectors";
import { ProductListPageContext } from "@mcmc/Overrides/Pages/ProductListPage";
import { productListStyles } from "@mcmc/Overrides/Widgets/ProductList/ListCommon.styles";
import StyledBox from "@mcmc/XDL/Components/Common/Box";
import EllipsisSpinner from "@mcmc/XDL/Components/Common/EllipsisSpinner";
import { Roboto } from "@mcmc/XDL/Utilities";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: McMcApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ query?: string; searchType?: string }>(location.search);
    const searchCountsData = getSearchCounts(state, parsedQuery.query);
    const productsPagination = getProductListDataViewProperty(state, "pagination");
    const contentPagesPagination = getContentPageDataViewProperty(state, "pagination");
    const isContentSearch = isContentSearchMode(state);
    let totalItemCount;
    if (isContentSearch) {
        totalItemCount = contentPagesPagination?.totalItemCount;
    } else {
        totalItemCount = productsPagination?.totalItemCount;
    }

    return {
        searchCountsData,
        search: parsedQuery.query,
        contentType: parsedQuery.searchType,
        totalItemCount,
        isSearchPage: isSearchPage(state),
    };
};
const mapDispatchToProps = {
    loadSearchCounts,
};

export interface SearchCountsStyles {
    container?: InjectableCss;
    headerTitle?: TypographyPresentationProps;
}

export const searchCountsStyles: SearchCountsStyles = {
    container: {
        css: css`
            border: none;
            margin-bottom: 40px;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            min-height: 150px;
        `,
    },
    headerTitle: {
        variant: "h3",
        weight: "bold",
        css: css`
            margin-bottom: 10px;
        `,
    },
};

const styles = searchCountsStyles;

type Props = HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const getLinkStyles = (isActive: boolean) => {
    return {
        typographyProps: {
            ...Roboto,
            size: 14,
            lineHeight: 1,
            weight: isActive ? "bold" : "normal",
            transform: "none",
        },
        css: css`
            color: ${getColor(isActive ? "primary" : "text")};
            margin: 5px 0;
            &:last-child {
                margin-bottom: 0;
            }
            &:hover {
                color: ${getColor(isActive ? "text" : "primary")};
                text-decoration: underline;
            }
        `,
    } as ClickablePresentationProps & { typographyProps: TypographyPresentationProps };
};

class SearchCounts extends React.Component<Props> {
    UNSAFE_componentWillMount(): void {
        if (
            this.props.search &&
            !this.props.searchCountsData?.searchCounts &&
            !this.props.searchCountsData?.isLoading
        ) {
            this.props.loadSearchCounts({ search: this.props.search });
        }
    }

    componentDidUpdate(prevProps: Props): void {
        if (
            this.props.search &&
            !this.props.searchCountsData?.searchCounts &&
            !this.props.searchCountsData?.isLoading
        ) {
            this.props.loadSearchCounts({ search: this.props.search });
        }
    }

    doSearch = (resultsSectionId: string) => {
        this.props.history.push(
            `/Search?query=${this.props.search}${resultsSectionId ? `&searchType=${resultsSectionId}` : ""}`,
        );
    };

    render() {
        const { searchCountsData, contentType, totalItemCount, isSearchPage } = this.props;
        if (!searchCountsData || !isSearchPage) {
            return null;
        }
        const { searchCounts, isLoading } = searchCountsData;

        const searchCountLinks = searchCounts
            ? Object.keys(searchCounts).map(contentTypeKey => {
                  let label = "";
                  let isActive = false;
                  let searchContentType = "";
                  switch (contentTypeKey) {
                      case "products":
                          label = "Products";
                          isActive =
                              contentType === SearchTypes.PRODUCTS ||
                              !contentType ||
                              [SearchTypes.EVENTS, SearchTypes.PAGES, SearchTypes.RESOURCES].indexOf(contentType) ===
                                  -1;
                          searchContentType = SearchTypes.PRODUCTS;
                          break;
                      case "events":
                          label = "Training & Events";
                          isActive = contentType === SearchTypes.EVENTS;
                          searchContentType = SearchTypes.EVENTS;
                          break;
                      case "pages":
                          label = "Pages";
                          isActive = contentType === SearchTypes.PAGES;
                          searchContentType = SearchTypes.PAGES;
                          break;
                      case "resources":
                          label = "Resources";
                          isActive = contentType === SearchTypes.RESOURCES;
                          searchContentType = SearchTypes.RESOURCES;
                          break;
                  }

                  if (isActive && totalItemCount !== undefined) {
                      label = translate(`${label} ({0})`, formatNumber(totalItemCount));
                  } else if (!isActive) {
                      label = translate(`${label} ({0})`, formatNumber((searchCounts as any)[contentTypeKey]));
                  }

                  return (
                      <Clickable
                          {...getLinkStyles(isActive)}
                          onClick={!isActive ? () => this.doSearch(searchContentType) : undefined}
                          key={contentTypeKey}
                      >
                          <EllipsisSpinner
                              label={label}
                              showSpinner={isActive && totalItemCount === undefined}
                              typographyProps={getLinkStyles(isActive).typographyProps}
                          />
                      </Clickable>
                  );
              })
            : undefined;

        return (
            <StyledBox {...styles.container}>
                <Typography {...styles.headerTitle}>{translate("Content Type")}</Typography>
                {isLoading ? <LoadingSpinner {...productListStyles.spinner} /> : searchCountLinks}
            </StyledBox>
        );
    }
}

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(SearchCounts)),
    definition: {
        group: "Product List",
        allowedContexts: [ProductListPageContext],
    },
};

export default widgetModule;
